import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, LogIn, Globe, Pill } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    const result = await login(username, password);
    
    if (result.success) {
      // Redirect based on role
      if (result.user?.role === 'inventory_admin') {
        navigate('/inventory');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(t('invalid_credentials'));
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Language Toggle */}
          <div className="flex justify-end mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="gap-2 rounded-full"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              {language === 'ar' ? 'English' : 'العربية'}
            </Button>
          </div>

          {/* Logo/Title */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 mb-4 shadow-lg">
              <Pill className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {t('app_title')}
            </h1>
          </div>

          <Card className="border-0 shadow-xl animate-fade-in stagger-1">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-semibold text-center">
                {t('login_title')}
              </CardTitle>
              <CardDescription className="text-center text-slate-500">
                {t('login_subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm animate-fade-in">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-700 font-medium">
                    {t('username')}
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('username')}
                    required
                    data-testid="login-username-input"
                    className="h-11 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">
                    {t('password')}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('password')}
                    required
                    data-testid="login-password-input"
                    className="h-11 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-medium gap-2 btn-active"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                >
                  {isLoading ? (
                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <LogIn className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                      {t('login')}
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 text-center">
                  {t('permissions_note')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Hospital Image */}
      <div 
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(/hospital-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Light blue transparent overlay */}
        <div className="absolute inset-0 bg-sky-600/30" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h2 className="text-4xl font-bold mb-4 animate-fade-in">
            {language === 'ar' ? 'نظام توثيق الأدوية' : 'Medication Documentation System'}
          </h2>
          <p className="text-lg text-sky-100 leading-relaxed animate-fade-in stagger-1">
            {t('intro_text')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
